package top.wcpe.allincore.ui.runtime.style

import top.wcpe.allincore.ui.runtime.render.model.GuiBlendMode
import kotlin.jvm.JvmName

/**
 * 作用域 / 包裹型视觉 modifier（ADR-0047）：成对 begin/end 包裹「元素的完整渲染（盒装饰 + 内容 + 子树）」，
 * 改变「内容如何合成」，区别于 [VisualModifier] 的逐图元 paint。
 *
 * 由 [top.wcpe.allincore.ui.runtime.render.ModifierDispatcher.dispatchScoped] 分派：列表顺序 begin（外→内）、
 * 逆序 end（内→外）。加一个作用域效果 = 加一个 `sealed` 子类型 + dispatchScoped 一个 `when` 分支（开闭）。
 */
sealed interface ScopedVisualModifier {

    /** filter:blur 高斯模糊：包裹的内容先渲到离屏 FBO，结束时两 pass 高斯后合成回主屏。[blurRadius] 为模糊半径（像素）。 */
    data class Blur(@get:JvmName("blurRadius") val blurRadius: Float) : ScopedVisualModifier

    /** 混合模式：包裹内容按 [mode] 与背景合成（CSS mix-blend-mode 子集）。 */
    data class BlendMode(@get:JvmName("mode") val mode: GuiBlendMode) : ScopedVisualModifier

    /**
     * 色彩矩阵 filter：对包裹内容套用 4×5 行优先色彩矩阵（亮度 / 对比 / 灰度 / 棕褐 / 色相 / 饱和 / 反相等）。
     *
     * [matrix4x5] 为长度 20 的行优先 4×5 矩阵。注意本类持有数组引用（不拷贝）——调用方不应在分派后改写它。
     */
    data class ColorFilter(@get:JvmName("matrix4x5") val matrix4x5: FloatArray) : ScopedVisualModifier {
        override fun equals(other: Any?): Boolean =
            this === other || (other is ColorFilter && matrix4x5.contentEquals(other.matrix4x5))

        override fun hashCode(): Int = matrix4x5.contentHashCode()
    }

    /**
     * 局部历史采样（FR-UI-023 historyLayer）：真正的 self-only 隔离须把元素自身绘制重定向到离屏靶，
     * 故建模为作用域型（与 [Blur] 同款 begin/end 包裹语义），而非逐图元 paint。
     *
     * [layer] 携带全部历史层参数（source / frames / downsample / decay / blur / distortion / opacity / blend / trigger）；
     * [elementId] 为元素跨帧稳定 id（环形缓冲键之一），由 [top.wcpe.allincore.ui.runtime.render.UIRenderer]
     * 在分派前按 `element.id() ?: "@"+identityHashCode` 注入，Style 侧构造时为 null。
     */
    data class History(
        @get:JvmName("layer") val layer: VisualModifier.HistoryLayer,
        @get:JvmName("elementId") val elementId: String? = null,
    ) : ScopedVisualModifier
}
